import React, { useEffect } from 'react';
import { Route, Routes } from 'react-router';
import LscLayout from './LscLayout';
import LscMarketingLayout from './LscMarketingLayout';
import LscLandingPage from './pages/LscLandingPage';
import LscLeagueListPage from './pages/LscLeagueListPage';
import LscCreateLeaguePage from './pages/LscCreateLeaguePage';
import LscLeagueDashboardPage from './pages/LscLeagueDashboardPage';
import LscLeagueSetupWizardPage from './pages/LscLeagueSetupWizardPage';
import LscCommandCenterPage from './pages/LscCommandCenterPage';
import LscMembersPage from './pages/LscMembersPage';
import LscScheduleImportPage from './pages/LscScheduleImportPage';
import LscMatchesPage from './pages/LscMatchesPage';
import LscVodSyncPage from './pages/LscVodSyncPage';
import LscCandidateDetailPage from './pages/LscCandidateDetailPage';
import LscNeedsAttentionPage from './pages/LscNeedsAttentionPage';
import LscArchiveListPage from './pages/LscArchiveListPage';
import LscArchiveDetailPage from './pages/LscArchiveDetailPage';
import RttsTab from '../features/rtts/RttsTab';
import { isRttsEnabled } from '../config/featureFlags';
import LscSettingsLayout from './pages/LscSettingsLayout';
import LscSettingsGeneralPage from './pages/LscSettingsGeneralPage';
import LscSettingsArchiveAutomationPage from './pages/LscSettingsArchiveAutomationPage';
import LscSettingsLiveAutomationPage from './pages/LscSettingsLiveAutomationPage';
import LscSettingsScheduleSourcePage from './pages/LscSettingsScheduleSourcePage';
import LscArchiveAutomationRedirect from './components/LscArchiveAutomationRedirect';
import LscCfbSetupPage from './pages/LscCfbSetupPage';
import LscCfbSchedulePage from './pages/LscCfbSchedulePage';
import LscCfbScheduleIntakePage from './pages/LscCfbScheduleIntakePage';
import LscAccountSettingsPage from './pages/LscAccountSettingsPage';
import LscJoinPage from './pages/LscJoinPage';
import LscJoinCodePage from './pages/LscJoinCodePage';
import LscNotFoundPage from './pages/LscNotFoundPage';
import LscPricingPage from './pages/LscPricingPage';
import LscBillingPage from './pages/LscBillingPage';
import LscBillingSuccessPage from './pages/LscBillingSuccessPage';
import LscBillingCanceledPage from './pages/LscBillingCanceledPage';
import LscAdminLayout from './pages/LscAdminLayout';
import LscAdminPage from './pages/LscAdminPage';
import LscAdminIssuesPage from './pages/LscAdminIssuesPage';
import LscAdminIssueDetailPage from './pages/LscAdminIssueDetailPage';
import LscAdminLeagueSummaryPage from './pages/LscAdminLeagueSummaryPage';
import LscAdminAutopublishNightlyPage from './pages/LscAdminAutopublishNightlyPage';
import { lscLayoutRoutePath } from './routing';
import { isLscAdminOnly } from '../config/featureFlags';
import { LSC_BILLING_ROUTE_PATHS } from './billingRoutePaths';
import LscLegacyPathRedirect from './components/LscLegacyPathRedirect';
import LscLeagueRouteRedirect from './components/LscLeagueRouteRedirect';

/** League Stream Center app shell — stream host root paths or /lsc fallback. */
const LscApp: React.FC = () => {
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  const layoutPath = lscLayoutRoutePath();

  return (
    <Routes>
      <Route path={layoutPath}>
        <Route
          index
          element={
            <LscMarketingLayout>
              <LscLandingPage />
            </LscMarketingLayout>
          }
        />
        {isLscAdminOnly() ? null : <Route path="lsc/*" element={<LscLegacyPathRedirect />} />}
        <Route element={<LscLayout />}>
          <Route path="settings" element={<LscAccountSettingsPage />} />
          <Route path={LSC_BILLING_ROUTE_PATHS.pricing} element={<LscPricingPage />} />
          <Route path={LSC_BILLING_ROUTE_PATHS.billing} element={<LscBillingPage />} />
          <Route
            path={LSC_BILLING_ROUTE_PATHS.billingSuccess}
            element={<LscBillingSuccessPage />}
          />
          <Route
            path={LSC_BILLING_ROUTE_PATHS.billingCanceled}
            element={<LscBillingCanceledPage />}
          />
          <Route path={LSC_BILLING_ROUTE_PATHS.lscPricing} element={<LscPricingPage />} />
          <Route path={LSC_BILLING_ROUTE_PATHS.lscBilling} element={<LscBillingPage />} />
          <Route
            path={LSC_BILLING_ROUTE_PATHS.lscBillingSuccess}
            element={<LscBillingSuccessPage />}
          />
          <Route
            path={LSC_BILLING_ROUTE_PATHS.lscBillingCanceled}
            element={<LscBillingCanceledPage />}
          />
          <Route path="join" element={<LscJoinPage />} />
          <Route path="join/:code" element={<LscJoinCodePage />} />
          <Route path="leagues" element={<LscLeagueListPage />} />
          <Route path="leagues/new" element={<LscCreateLeaguePage />} />
          <Route path="admin" element={<LscAdminLayout />}>
            <Route index element={<LscAdminPage />} />
            <Route path="issues" element={<LscAdminIssuesPage />} />
            <Route path="issues/:issueId" element={<LscAdminIssueDetailPage />} />
            <Route path="leagues/:leagueId" element={<LscAdminLeagueSummaryPage />} />
            <Route path="autopublish" element={<LscAdminAutopublishNightlyPage />} />
          </Route>
          <Route path="leagues/:leagueId" element={<LscLeagueDashboardPage />} />
          <Route path="leagues/:leagueId/setup" element={<LscLeagueSetupWizardPage />} />
          <Route path="leagues/:leagueId/command-center" element={<LscCommandCenterPage />} />
          <Route path="leagues/:leagueId/members" element={<LscMembersPage />} />
          <Route path="leagues/:leagueId/import" element={<LscScheduleImportPage />} />
          <Route path="leagues/:leagueId/cfb/setup" element={<LscCfbSetupPage />} />
          <Route path="leagues/:leagueId/cfb/schedule" element={<LscCfbSchedulePage />} />
          <Route path="leagues/:leagueId/cfb/schedule/intake" element={<LscCfbScheduleIntakePage />} />
          <Route path="leagues/:leagueId/matches" element={<LscMatchesPage />} />
          <Route
            path="leagues/:leagueId/schedule"
            element={<LscLeagueRouteRedirect toSegment="matches" />}
          />
          <Route path="leagues/:leagueId/vods" element={<LscVodSyncPage />} />
          <Route
            path="leagues/:leagueId/vods/:candidateId"
            element={<LscCandidateDetailPage />}
          />
          <Route path="leagues/:leagueId/needs-attention" element={<LscNeedsAttentionPage />} />
          <Route
            path="leagues/:leagueId/archive-automation"
            element={<LscArchiveAutomationRedirect />}
          />
          <Route
            path="leagues/:leagueId/review"
            element={<LscLeagueRouteRedirect toSegment="needs-attention" />}
          />
          <Route
            path="leagues/:leagueId/archives"
            element={<LscLeagueRouteRedirect toSegment="archive" />}
          />
          <Route path="leagues/:leagueId/archive" element={<LscArchiveListPage />} />
          {isRttsEnabled() ? (
            <Route path="leagues/:leagueId/rtts" element={<RttsTab />} />
          ) : null}
          <Route
            path="leagues/:leagueId/archives/:archiveId"
            element={<LscLeagueRouteRedirect toSegment="archive" />}
          />
          <Route
            path="leagues/:leagueId/archive/:archiveId"
            element={<LscArchiveDetailPage />}
          />
          <Route path="leagues/:leagueId/settings" element={<LscSettingsLayout />}>
            <Route index element={<LscSettingsGeneralPage />} />
            <Route path="schedule-source" element={<LscSettingsScheduleSourcePage />} />
            <Route path="archive-automation" element={<LscSettingsArchiveAutomationPage />} />
            <Route path="live-automation" element={<LscSettingsLiveAutomationPage />} />
          </Route>
          <Route path="*" element={<LscNotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default LscApp;
